import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { MdFlag, MdClose, MdCheck } from 'react-icons/md';
import axios from '../config/api';
import { API_URL } from '../config/api';

const REPORT_REASONS = [
  { id: 'misleading', label: 'Misleading or sensational' },
  { id: 'violent', label: 'Violent or repulsive' },
  { id: 'hateful', label: 'Hateful or abusive' },
  { id: 'intrusive', label: 'Intrusive or too personal' },
  { id: 'other', label: 'Other' }
];

function ReportButton({ pollId, isCustomPoll = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const reasonLabel = REPORT_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
      const endpoint = isCustomPoll 
        ? `${API_URL}/api/custom-polls/${pollId}/flag`
        : `${API_URL}/api/polls/${pollId}/flag`;
      await axios.post(endpoint, { reason: reasonLabel });
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setSelectedReason('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedReason('');
    setError('');
    setSubmitted(false);
  };

  const modal = isOpen ? createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-card shadow-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
        data-testid="report-modal"
      >
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <MdCheck className="text-white text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2 font-rubik">Thank You!</h2>
            <p className="text-gray-600 font-rubik">Your report has been submitted for admin review.</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800 font-rubik flex items-center gap-2">
                <MdFlag className="text-danger" />
                Report this Poll
              </h2>
              <button 
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="close-report-modal"
              >
                <MdClose className="text-2xl" />
              </button>
            </div>

            <p className="text-sm text-gray-600 font-rubik mb-4">
              Please select the reason for reporting this poll:
            </p>

            <div className="space-y-3 mb-6">
              {REPORT_REASONS.map(reason => (
                <label 
                  key={reason.id}
                  className={`flex items-center p-3 border rounded-card cursor-pointer transition-all ${
                    selectedReason === reason.id 
                      ? 'border-danger bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`report-reason-${reason.id}`}
                >
                  <input
                    type="radio"
                    name="reportReason"
                    value={reason.id}
                    checked={selectedReason === reason.id}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-4 h-4 text-danger focus:ring-danger"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700 font-rubik">
                    {reason.label}
                  </span>
                </label>
              ))}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-danger rounded-card">
                <p className="text-sm text-danger font-rubik">{error}</p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting || !selectedReason}
              className="w-full btn-danger py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-report-button"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="text-gray-400 hover:text-danger text-sm font-medium flex items-center gap-1 font-rubik transition-colors"
        data-testid="report-poll-button"
      >
        <MdFlag className="text-base" />
        Report
      </button>
      {modal}
    </>
  );
}

export default ReportButton;
